import { hasOneOfPermission, sendUserAudit } from '../../../store/reducers/user';
import { AgyleTab, AgyleTabs } from '../../../components/ui/tabs/agyleTabs';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/moment';
import { AgyleNamedIcon, AgyleNeuroObsIcon } from '../../../components/icons/agyle';
import clsx from 'clsx';
import { getConfig } from '../../../util/configUtil';
import { getAdmissionStartDate } from '../../../util/patientUtil';
import makeStyles from '@mui/styles/makeStyles';
import {
    changeActiveContextScreen,
    changePatientContextTab,
    ctxCurrentTab,
    ctxFormIsOpen,
    ctxPatientSpecialTags,
    ctxSplitScreen,
    setFormIsOpen,
    setObservationType,
} from '../../../store/reducers/patientContext';
import { agyleBlockContext, setSnackbar } from '../../../store/reducers/globals';
import useOpenState from '../../../hooks/useOpenState';
import MessageBox from '../../../util/messageBox';
import { PATIENT_CONTEXT_ITEMS } from './components/forms/componentList';
import { additionalData } from '../../../store/reducers/additionalData';

import { SepsisPathwayStatus } from '../../ward/sepsis/sepsisUtil';
import { useTheme } from '@mui/styles';
import { PATIENT_CONTEXT_TABS_ID } from '../../ward/forms/formsUtil';
import { AUDIT_TYPES } from '../../../util/auditUtil';

const useStyles = makeStyles((theme) => ({
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    totalScoreDiv: {
        border: `1px solid ${theme.palette.observation.border}`,
        color: theme.palette.observation.text,
        borderRadius: 20,
        textAlign: 'center',
        width: 22,
        height: 20,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
    tabIcon: {
        fontSize: 22,
        color: '#dc4a5d',
        '&.orange': {
            color: '#F26B28',
        },
        '&.green': {
            color: '#01AF69',
        },
        '&.red': {
            color: '#dc4a5d',
        },
    },
}));

const ContextTabs = ({ patient, patientType, pastAttendances }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();

    // Selectors
    const currentTab = useSelector(ctxCurrentTab);
    const blockContext = useSelector(agyleBlockContext);
    const formIsOpen = useSelector(ctxFormIsOpen);
    const patientAddData = useSelector(additionalData);
    const splitScreen = useSelector(ctxSplitScreen);
    const patientTags = useSelector(ctxPatientSpecialTags);

    // Config
    const showIconOnTab = getConfig('observations.showIconOnTab', false);
    const sepsisShowTabIcon = getConfig('sepsisShowTabIcon', false);

    const [currentTabTmp, setCurrentTabTmp] = useState(null);
    const [isWarningLostDataOpen, openWarningLostData, closeWarningLostData] = useOpenState(false, true);
    const [incompleteSepsisPath, setIncompleteSepsisPath] = useState(false);

    useEffect(() => {
        if (patientAddData.other?.sepsisPathways) {
            setIncompleteSepsisPath(!!_.find(patientAddData.other.sepsisPathways, ['statusObj', SepsisPathwayStatus.INCOMPLETE]));
        } else {
            setIncompleteSepsisPath(false);
        }
    }, [patientAddData]);

    const auditTabChange = (tab) => {
        if (tab === PATIENT_CONTEXT_TABS_ID.UCR_FILTERED || tab === PATIENT_CONTEXT_TABS_ID.UCR) {
            dispatch(sendUserAudit(patient, AUDIT_TYPES.OPEN_UCR, { clinicalLetters: tab === PATIENT_CONTEXT_TABS_ID.UCR_FILTERED }));
        } else if (tab === PATIENT_CONTEXT_TABS_ID.DNACPR) {
            dispatch(sendUserAudit(patient, AUDIT_TYPES.OPEN_DNACPR));
        }
    };

    const handleTabChange = (_e, newTabValue) => {
        if (splitScreen.enabled) {
            if (newTabValue === PATIENT_CONTEXT_TABS_ID.OBSERVATIONS) {
                dispatch(setObservationType('obs'));
            }
            // TODO @Mateusz Display warning when full form is open !
            auditTabChange(newTabValue);
            dispatch(changeActiveContextScreen(newTabValue));
        } else {
            if (!formIsOpen && !blockContext) {
                if (newTabValue === PATIENT_CONTEXT_TABS_ID.OBSERVATIONS) {
                    dispatch(setObservationType('obs'));
                }
                auditTabChange(newTabValue);
                dispatch(changePatientContextTab(newTabValue));
            } else if (blockContext) {
                dispatch(
                    setSnackbar({
                        type: 'warning',
                        message: t('Close the current sepsis pathway to perform this operation.'),
                    }),
                );
            } else {
                openWarningLostData();
                setCurrentTabTmp(newTabValue);
            }
        }
    };

    const getObsLabel = () => {
        if (!showIconOnTab) {
            return t('Observations');
        } else {
            // Border Color
            const age = moment.duration(getAdmissionStartDate(patient?.admission).diff(moment(patient?.patient?.dob))).asYears();
            const isAdult = age >= 16;
            let obsColor;
            const score = patient?.patient?.lastObservation?.totalScore;

            const themeColors = theme?.palette?.observation;
            if (isAdult) {
                if (score >= 9) obsColor = themeColors?.red;
                else if (score < 9 && score >= 7) obsColor = themeColors?.amber;
                else if (score < 7 && score >= 5) obsColor = themeColors?.yellow;
                else obsColor = themeColors?.white;
            } else {
                if (score >= 13) obsColor = themeColors?.red;
                else if (score < 13 && score >= 9) obsColor = themeColors?.pink;
                else if (score < 9 && score >= 5) obsColor = themeColors?.amber;
                else if (score < 5 && score >= 1) obsColor = themeColors?.yellow;
                else obsColor = themeColors?.white;
            }

            if (patient?.metadata?.nextNeuroObs) {
                let status = '';
                const time = moment(patient?.metadata?.nextNeuroObs?.nextObs).diff(moment(), 'minutes');
                if (time <= 0) status = 'red';
                else if (time < 15) status = 'orange';
                else status = 'green';

                return (
                    <div className={classes.divRow} style={{ gap: 3 }}>
                        {t('Observations')}
                        {score >= 0 && (
                            <div className={classes.totalScoreDiv} style={{ backgroundColor: obsColor }}>
                                {score}
                            </div>
                        )}
                        <AgyleNeuroObsIcon className={clsx(classes.tabIcon, status)} />
                    </div>
                );
            }

            return (
                <div className={classes.divRow} style={{ gap: 3 }}>
                    {t('Observations')}
                    {score >= 0 && (
                        <div className={classes.totalScoreDiv} style={{ backgroundColor: obsColor }}>
                            {score}
                        </div>
                    )}
                </div>
            );
        }
    };

    const getSepsisTitle = () => {
        if (incompleteSepsisPath) {
            return (
                <div className={classes.divRow}>
                    {t('Sepsis Pathway')}
                    {sepsisShowTabIcon && (
                        <AgyleNamedIcon
                            name={incompleteSepsisPath?.neutropenic ? 'neutropenicSepsis' : 'sepsis'}
                            className={clsx(classes.tabIcon, 'sepsis')}
                        />
                    )}
                </div>
            );
        }
        return t('Sepsis Pathway');
    };

    const getCustomLabel = (id) => {
        switch (id) {
            case PATIENT_CONTEXT_TABS_ID.OBSERVATIONS:
                return getObsLabel();
            case PATIENT_CONTEXT_TABS_ID.SEPSIS:
                return getSepsisTitle();

            default:
                console.error('Wrong context tab id', id);
                return '-ERROR-';
        }
    };

    const closeDialogForm = () => {
        auditTabChange(currentTabTmp);
        closeWarningLostData();
        dispatch(changePatientContextTab(currentTabTmp));
        dispatch(setFormIsOpen(false));
    };

    const getTabValue = () => {
        if (splitScreen.enabled) {
            return splitScreen.activeScreen === 1 ? splitScreen.screenOne : splitScreen.screenTwo;
        }
        return currentTab;
    };

    return (
        <>
            <AgyleTabs style={{ paddingLeft: 5 }} value={getTabValue()} variant="scrollable" onChange={handleTabChange}>
                {PATIENT_CONTEXT_ITEMS.map((item) => {
                    let visible = item.enabled;

                    if (
                        visible &&
                        item.options.disabledForPermissions?.length &&
                        dispatch(hasOneOfPermission(item.options.disabledForPermissions))
                    ) {
                        visible = false;
                    }

                    if (visible && !_.isEmpty(item.excludePatientType)) {
                        visible = !_.some(item.excludePatientType, (type) => type === patientType);
                    }

                    const disableTags = _.get(item, 'options.disableForPatientTags', []);
                    if (visible && !_.isEmpty(disableTags) && !_.isEmpty(patientTags)) {
                        visible = !_.some(disableTags, (tag) => _.includes(patientTags, tag));
                    }
                    if (!visible || !item?.options?.showOnTab || (pastAttendances && item?.options?.hideOnPastActivity)) return null;

                    let disabled = false;
                    if (!_.isEmpty(item.requiredPermission)) {
                        disabled = !dispatch(hasOneOfPermission(item.requiredPermission));
                    }

                    return (
                        <AgyleTab
                            key={item.id}
                            label={_.isString(item.tabDescription) ? t(item.tabDescription) : getCustomLabel(item.id)}
                            value={item.id}
                            disabled={disabled}
                        />
                    );
                })}
            </AgyleTabs>

            <MessageBox
                open={isWarningLostDataOpen}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                onAccept={closeDialogForm}
                onClose={closeWarningLostData}
                message={t(
                    'Are you sure you want to exit this open form? Changes not saved to a draft or submitted document will be lost!',
                )}
            />
        </>
    );
};

export default ContextTabs;
